import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils.ts';

/**
 * Pill buttons — the website's CTA shape (brand board "Planner components").
 *  primary   = pasture fill (one per view, the main action)
 *  secondary = white with a hairline
 *  quiet     = text-only link-style action
 *  danger    = destructive (void, discard) — always with a clear label
 */
export const buttonVariants = cva(
  'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full border border-transparent font-semibold transition-colors outline-none select-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-on-accent hover:bg-accent-hover',
        secondary: 'border-line-strong bg-surface text-ink hover:border-ink-3',
        quiet: 'bg-transparent px-1 font-medium text-accent-ink underline-offset-2 hover:underline',
        ghost: 'bg-transparent text-ink-2 hover:bg-surface-2 hover:text-ink',
        danger: 'bg-critical-wash text-critical-ink hover:bg-critical hover:text-white',
      },
      size: {
        sm: 'h-8 px-3 text-label [&_svg]:size-3.5',
        md: 'h-10 px-4 text-table [&_svg]:size-4',
        lg: 'h-12 px-6 text-body [&_svg]:size-5',
        // Barn phones: big, gloved-thumb friendly.
        xl: 'h-16 px-8 text-[18px] [&_svg]:size-6',
        icon: 'size-10 [&_svg]:size-5',
        'icon-sm': 'size-8 [&_svg]:size-4',
      },
    },
    compoundVariants: [{ variant: 'quiet', className: 'h-auto px-1' }],
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    /** Render the child element (e.g. a router <Link>) with button styling. */
    asChild?: boolean;
  };

export function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...(asChild ? {} : { type: props.type ?? 'button' })}
      {...props}
    />
  );
}
